/**
 * CRC32-ISO/HDLC implementation for Ediphor device communication
 *
 * Polynomial: 0x04C11DB7 (reversed: 0xEDB88320)
 * Initial value: 0xFFFFFFFF
 * Final XOR: 0xFFFFFFFF
 *
 * This is the same CRC32 used in Ethernet, HDLC, zlib, PNG, etc.
 */

// Pre-computed CRC32 lookup table (IEEE 802.3 polynomial, bit-reversed)
const CRC32_TABLE = new Uint32Array(256);

// Initialize the lookup table
(function initCRC32Table() {
  const POLYNOMIAL = 0xedb88320;
  for (let i = 0; i < 256; i++) {
    let crc = i;
    for (let j = 0; j < 8; j++) {
      crc = crc & 1 ? (crc >>> 1) ^ POLYNOMIAL : crc >>> 1;
    }
    CRC32_TABLE[i] = crc >>> 0; // Ensure unsigned
  }
})();

/**
 * Calculate CRC32-ISO checksum of data
 * @param data - Input bytes
 * @returns CRC32 checksum as unsigned 32-bit integer
 */
export function calculateCRC32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = (crc >>> 8) ^ CRC32_TABLE[(crc ^ data[i]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0; // Final XOR and ensure unsigned
}

/**
 * Append CRC32 checksum to data (big-endian / network byte order)
 * @param data - Protobuf encoded message
 * @returns Data with 4-byte CRC32 appended
 */
export function appendCRC32(data: Uint8Array): Uint8Array {
  const crc = calculateCRC32(data);
  const result = new Uint8Array(data.length + 4);
  result.set(data);

  // Append CRC as big-endian 4 bytes (MSB first)
  result[data.length] = (crc >>> 24) & 0xff;
  result[data.length + 1] = (crc >>> 16) & 0xff;
  result[data.length + 2] = (crc >>> 8) & 0xff;
  result[data.length + 3] = crc & 0xff;

  return result;
}

/**
 * Result of CRC32 verification
 */
export interface CRC32VerifyResult {
  /** Whether CRC matched */
  valid: boolean;
  /** Payload data (without CRC bytes) */
  payload: Uint8Array;
  /** Received CRC value */
  receivedCrc: number;
  /** Calculated CRC value */
  calculatedCrc: number;
}

/**
 * Verify and strip CRC32 from received data
 * @param data - Received bytes with CRC32 appended
 * @returns Verification result with payload
 * @throws Error if data is too short to contain CRC
 */
export function verifyCRC32(data: Uint8Array): CRC32VerifyResult {
  if (data.length < 4) {
    throw new Error('Data too short to contain CRC32 checksum');
  }

  const payload = data.slice(0, -4);

  // Extract received CRC (big-endian / network byte order)
  const receivedCrc =
    ((data[data.length - 4] << 24) |
      (data[data.length - 3] << 16) |
      (data[data.length - 2] << 8) |
      data[data.length - 1]) >>>
    0; // Ensure unsigned

  const calculatedCrc = calculateCRC32(payload);

  return {
    valid: receivedCrc === calculatedCrc,
    payload,
    receivedCrc,
    calculatedCrc,
  };
}

/**
 * Strip CRC32 from data without verification (use with caution)
 * @param data - Data with CRC32 appended
 * @returns Data without the trailing 4 bytes
 */
export function stripCRC32(data: Uint8Array): Uint8Array {
  if (data.length < 4) {
    throw new Error('Data too short to contain CRC32 checksum');
  }
  return data.slice(0, -4);
}
